import { useMemo } from 'react'

import { getCurrency } from '@common/utils/currency.ts'
import { useUnit } from 'effector-react'
import { useQuery } from 'react-query'

import { Page } from '@/components/layout/page/page.tsx'
import { useDateRangeContext } from '@/components/provider/date-range-provider.tsx'
import { CalendarRangeDatePicker } from '@/components/ui/calendar/calendar-range-date-picker.tsx'
import { WithRefresher } from '@/components/ui/with-refresher.tsx'
import { $currentCreator } from '@/models/creators.ts'
import { useAccount } from '@/modules/accounts/hooks/useAccounts.ts'
import { TotalChart } from '@/modules/overview/ui/TotalChart'
import {
  mapNewSubscriptionsData,
  mapRevenueData,
} from '@/modules/overview/utils'
import { statisticsService } from '@/modules/statistics/onlyfans/service/statistics-service.ts'
import { CustomerLifetimeValue } from '@/modules/statistics/onlyfans/ui/CustomerLifetimeValue.tsx'
import { formatNumberWithCommas } from '@/utils/helpers'

import { BestTimeChart } from '../ui/BestTimeChart'
import { CompositionLineChart } from '../ui/CompositionLineChart'
import { DetailedStatistics } from '../ui/DetailedStatistics'
import { TopFansStats } from '../ui/TopFansStats'
import { Heatmap } from '../ui/heatmap'
import { mapBestDayData, mapBestHourData, mapIncomeSourcesData } from '../utils'

export const StatisticsOnlyFansView = () => {
  const activeCreator = useUnit($currentCreator)
  const { userSettings } = useAccount()
  const shouldShowCents = useMemo(
    () => userSettings?.showCents,
    [userSettings?.showCents],
  )
  const { date, setDate, isOneDay, today } = useDateRangeContext()

  const {
    data,
    isFetching,
    refetch: refetchAllTracking,
  } = useQuery(
    statisticsService.getStatisticsKey(
      [activeCreator?.id ?? 0],
      date.from!,
      date.to!,
    ),
    () => {
      if (!date || !activeCreator) return

      return statisticsService.getCreatorStats(
        [activeCreator.id],
        date.from || today,
        date.to || today,
      )
    },
  )

  const { revenueData, subscriptionsData, incomeSources } = useMemo(
    () => ({
      revenueData: mapRevenueData(data, true, false, isOneDay),
      subscriptionsData: mapNewSubscriptionsData(data, true, false, isOneDay),
      incomeSources: mapIncomeSourcesData(data, isOneDay),
    }),
    [isOneDay, data],
  )

  const {
    data: fanStatisticsData,
    isFetching: isFanStatisticsLoading,
    refetch: refetchCreatorFansStats,
  } = useQuery(
    ['fan-statistics', activeCreator, date],
    () => {
      return statisticsService.getCreatorFanStats(
        activeCreator!.id,
        date.from || new Date(),
        date.to || new Date(),
        0,
        5,
      )
    },
    {
      enabled: !!date && !!activeCreator,
    },
  )

  const customerLifetimeQuery = useQuery(
    ['customer-lifetime-value', activeCreator, date],
    () => {
      return statisticsService.getCreatorCustomerLifetimeValue(
        activeCreator!.id,
        date.from || new Date(),
        date.to || new Date(),
      )
    },
    {
      enabled: !!date && !!activeCreator,
    },
  )

  const handleRefresh = () => {
    refetchCreatorFansStats()
    customerLifetimeQuery.refetch()
    refetchAllTracking()
  }

  const notLoading = !date || !activeCreator

  return (
    <Page
      title="OnlyFans Statistics"
      headerOptions={
        <WithRefresher
          onRefresh={handleRefresh}
          isFetching={isFanStatisticsLoading || isFetching || notLoading}
        >
          <CalendarRangeDatePicker
            date={date}
            setDate={setDate}
            dateNameSuffix={
              data?.timezoneOffset ? `(${data?.timezoneOffset})` : undefined
            }
          />
        </WithRefresher>
      }
    >
      <div className="grid gap-8">
        <div className="grid grid-flow-row grid-cols-1 gap-4 xl:grid-cols-2 xl:gap-8">
          <TotalChart
            title="Turnover"
            totalValue={getCurrency(
              data?.total.totalRevenue,
              shouldShowCents ? 2 : 0,
            )}
            variant="big"
            data={revenueData}
            isFetching={isFetching || notLoading}
            isCurrency
            showCents={shouldShowCents}
          />
          <TotalChart
            title="Subscriptions"
            totalValue={formatNumberWithCommas(
              data?.total.newSubscriptions || 0,
            )}
            variant="big"
            data={subscriptionsData}
            isFetching={isFetching || notLoading}
          />
        </div>

        <div className="grid grid-flow-row grid-cols-1 gap-4 xl:grid-cols-2 xl:gap-8">
          <CompositionLineChart
            title="Composition of Income Sources"
            data={incomeSources}
            isFetching={isFetching || notLoading}
            showLegend
            showCents={shouldShowCents}
          />
          <TopFansStats
            topFanList={fanStatisticsData}
            isFetching={isFanStatisticsLoading || notLoading}
            showCents={shouldShowCents}
          />
        </div>
        <div className="grid grid-flow-row grid-cols-1 gap-4 xl:grid-cols-2 xl:gap-8">
          <BestTimeChart
            title="Best Day"
            data={mapBestDayData(data)}
            isFetching={isFetching || notLoading}
            showCents={shouldShowCents}
          />
          <BestTimeChart
            title="Best Hour"
            data={mapBestHourData(data)}
            isFetching={isFetching || notLoading}
            showCents={shouldShowCents}
          />
        </div>

        <CustomerLifetimeValue
          isFetching={customerLifetimeQuery.isFetching}
          value={customerLifetimeQuery.data}
        />
        <Heatmap
          isFetching={isFetching || notLoading}
          fanActivityHeatmap={data?.fanActivityHeatmap}
          earningsHeatmap={data?.earningsHeatmap}
        />
        <DetailedStatistics
          showCents={shouldShowCents}
          creatorId={activeCreator?.id}
          date={date}
        />
      </div>
    </Page>
  )
}
